import { WhereItIs, fullLabel } from "../components/WhereItIs";
import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import { fromState } from "@/lib/nav";
import api, { formatApiErrorDetail } from "@/lib/api";
import { prov } from "@/lib/provenance";
import { CreatePartFromTruck } from "@/components/CreatePartFromTruck";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PhotoGallery } from "@/components/PhotoGallery";
import { PhotoMoveTrail } from "@/components/PhotoFixAction";
import { BuildSheetViewer } from "@/components/BuildSheetViewer";
import { DataplatePanel } from "@/components/DataplatePanel";
import { SheetReaderPanel } from "@/components/SheetReaderPanel";
import { ScrapDialog, ScrapTruckDialog } from "@/components/ExitActions";
import { DonorTagButton } from "@/components/DonorTagButton";
import { ScanField } from "@/components/ScanField";
import { PhotoUploader } from "@/components/PhotoUploader";
import { MoreActions } from "@/components/MoreActions";
import { SalesMenuButton } from "@/components/sales/SalesMenu";
import { DuplicateOfAction, MergeHistory, MergeUndoLine } from "@/components/TruckMergeDialog";
import { formatDate } from "@/lib/datetime";
import { YardSectorMove } from "@/components/YardSectorMove";
import { BackLink } from "@/components/BackLink";
import { titleCase } from "@/lib/text";
import { NotesLog } from "@/components/NotesLog";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { toast } from "sonner";
import { statusLabel, statusColor, partStatusLabel, partStatusColor, isConsumedPart } from "@/lib/status";
import { TruckSpecsPanel } from "@/components/TruckSpecsPanel";
import { TruckRoleTag } from "@/components/TruckRoleTag";
import { TruckSellableList } from "@/components/TruckSellableList";
import { TruckKeywords } from "@/components/TruckKeywords";
import { RefusedUploads } from "@/components/RefusedUploads";
import { useDonorStatuses, useDonorStageLabel } from "@/lib/vehicleLists";
import {
  ArrowLeft, Truck, Camera, FileText, Pencil, Package, PackagePlus, ShoppingCart, AlertTriangle, Trash2,
  DollarSign, Lock, Loader2, MapPin, Gauge, Clock, ExternalLink, Download, Inbox, Wrench, RefreshCw, GitMerge, ChevronDown, Tag, Cpu, FileSpreadsheet, Sparkles } from "lucide-react";

// What the truck sticker shows, in his words, for the "tag out of date" line below.
const TAG_FIELD_LABELS = { stock_number: "stock number", year: "year", make: "make", model: "model", vin: "VIN" };

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

function SectionCard({ title, icon: Icon, action, children, testid, collapsibleId, defaultOpen = true, openSignal }) {
  const storeKey = collapsibleId ? `truck-section-open:${collapsibleId}` : null;
  const [open, setOpen] = useState(() => {
    if (!storeKey) return true;
    const v = localStorage.getItem(storeKey);
    return v === null ? defaultOpen : v === "1";
  });
  useEffect(() => { if (openSignal) setOpen(true); }, [openSignal]);
  const toggle = () => {
    if (!collapsibleId) return;
    setOpen((o) => { const n = !o; localStorage.setItem(storeKey, n ? "1" : "0"); return n; });
  };
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <button type="button" onClick={toggle} disabled={!collapsibleId}
          data-testid={collapsibleId ? `section-toggle-${collapsibleId}` : undefined}
          className={`flex items-center gap-2 font-display font-bold text-slate-800 ${collapsibleId ? "cursor-pointer hover:text-[#B01E28]" : "cursor-default"}`}>
          {collapsibleId && <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} />}
          {Icon && <Icon className="w-4 h-4 text-[#B01E28]" />}{title}
        </button>
        {open && action}
      </div>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
}

function Field({ label, value, mono, chip, note }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-sm text-slate-800 mt-0.5 flex items-center gap-1.5 ${mono ? "font-mono" : ""}`}>
        {value || <span className="text-slate-300">—</span>}{chip}
      </div>
      {note}
    </div>
  );
}

function SourceChip({ src, meta }) {
  if (!src) return null;
  // "Corrected by a person" has to be unmistakable — it is the whole point of the fix: the value
  // no longer matches the decode, and a person did that on purpose.
  const map = {
    decoded: ["bg-blue-50 text-blue-700 border-blue-200", "decoded"],
    corrected: ["bg-amber-100 text-amber-800 border-amber-300", "corrected by a person"],
    confirmed: ["bg-emerald-50 text-emerald-700 border-emerald-200", "checked by a person"],
    manual: ["bg-emerald-50 text-emerald-700 border-emerald-200", "entered by a person"],
  };
  const [cls, text] = map[src] || map.manual;
  const who = meta?.by ? ` · ${meta.by.split("@")[0]}${meta.at ? ` ${meta.at.slice(0, 10)}` : ""}` : "";
  return <span data-testid={`source-chip-${src}`} title={`${text}${who}`}
    className={`text-[8px] font-mono uppercase px-1 py-0.5 border rounded ${cls}`}>{text}</span>;
}

export default function DonorDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const mergedFrom = searchParams.get("merged_from");
  const { has } = useAuth();
  const canRequest = has("submit_request");
  const canManage = has("manage_donors");
  const canArchivePhotos = has("archive_photos");
  const canCaptureParts = has("manage_parts");
  const canScrap = has("scrap_writeoff");
  const canPrintTags = has("generate_print_labels");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPartOpen, setNewPartOpen] = useState(false);
  const [refusedTick, setRefusedTick] = useState(0);
  const bumpRefused = () => setRefusedTick((t) => t + 1);
  const [dealerSheet, setDealerSheet] = useState(null);   // an Excel picked on "Build Sheet" → read exactly first
  const [roleConfirm, setRoleConfirm] = useState(null);  // target role pending confirmation
  const [poInput, setPoInput] = useState("");
  const [switching, setSwitching] = useState(false);
  const [capBusy, setCapBusy] = useState(false);

  const captureIntoDonor = async (tag_code, meta) => {
    setCapBusy(true);
    try {
      const { data: res } = await api.post(`/donors/${id}/capture`, { tag_code,
                                                                      provenance: prov("capture", meta) });
      scanAccept(`${res.part.short_code} CAPTURED`);
      toast.success(`${res.part.short_code} captured into this truck — identify it in Identify & Completion`);
      silentReload();   // never the full-page spinner: it would unmount the box and switch the camera off
    } catch (e) {
      scanRefuse(formatApiErrorDetail(e.response?.data?.detail));
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setCapBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/donors/${id}/detail`); setData(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const silentReload = async () => {
    try { const { data } = await api.get(`/donors/${id}/detail`); setData(data); } catch { /* keep current view */ }
  };
  const addDonorPhoto = (photo) => {
    if (!photo?.file_id) return;
    setData((prev) => (prev ? { ...prev, donor: { ...prev.donor, photos: [...(prev.donor.photos || []), photo] } } : prev));
  };
  const [attachFileId, setAttachFileId] = useState(null);   // check-in photo pending "add to a part"
  const [attaching, setAttaching] = useState(false);
  // Arriving from the "Wrong photo?" refusal, which sends a truck photo here rather than telling
  // the tech to find this dialog themselves. The param is consumed once and dropped.
  useEffect(() => {
    const fid = searchParams.get("attach");
    if (fid) { setAttachFileId(fid); setSearchParams((p) => { p.delete("attach"); return p; }, { replace: true }); }
    /* eslint-disable-next-line */
  }, [searchParams]);
  const [photoFilter, setPhotoFilter] = useState("all");    // gallery filter: all | checkin | dismantle
  const attachToPart = async (partId) => {
    setAttaching(true);
    try {
      const { data: res } = await api.post(`/donors/${id}/photos/${attachFileId}/attach-to-part`, { part_id: partId });
      toast.success(`Added to part ${res.tag_code || ""} — the check-in photo stays here too`);
      setAttachFileId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAttaching(false); }
  };
  const switchRole = async (toRole) => {
    if (toRole === "builder" && !poInput.trim() && !d.po_number) {
      toast.error("A builder needs a Purchase Order (PO). Enter the PO from the invoicing system.");
      return;
    }
    setSwitching(true);
    try {
      await api.post(`/donors/${id}/role`, { role: toRole, po_number: poInput.trim() || undefined });
      toast.success(`Now ${toRole === "builder" ? "a Builder" : "Parts"}`); setRoleConfirm(null); setPoInput(""); load();
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSwitching(false); }
  };

  const donorStages = useDonorStatuses();
  const donorStatusLabel = useDonorStageLabel();
  const DONOR_STATUSES = (donorStages.length ? donorStages.map((s) => s.code) : [data?.donor?.status].filter(Boolean));
  const [stageSaving, setStageSaving] = useState(false);
  const setStage = async (status) => {
    if (status === data.donor.status) return;
    setStageSaving(true);
    try { await api.post(`/donors/${id}/status`, { status }); toast.success(`Stage → ${donorStatusLabel(status)}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setStageSaving(false); }
  };
  // ─── A DEAD SECOND EDIT SCREEN LIVED HERE, AND IT IS WHY HE COULD NOT FIX AN ENGINE ───
  // `openEdit` was the only thing that set `editOpen`, and NOTHING called `openEdit` — the Edit
  // button navigates to the dedicated edit workspace instead. So this dialog could never open,
  // and the Engine box it still contained was unreachable. When the workspace replaced it
  // (2026-07-27) Year, Make, Model, Mileage and Hours were carried across and ENGINE WAS NOT, so
  // the decode could write an engine that no screen on earth could change. It also duplicated
  // every `donor-edit-*` test id with the real edit page, which is how a test could pass against
  // a screen a person can never reach. Removed, so there is ONE place a vehicle is edited.

  if (loading) return <div data-testid="donor-detail-loading" className="p-16 text-center font-mono text-xs text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />LOADING…</div>;
  if (!data) return <div data-testid="donor-detail-missing" className="p-16 text-center text-sm text-slate-500">Truck not found. <Link to="/donors" className="text-[#E0781A] underline">Back to Vehicles &amp; Equipment</Link></div>;

  const d = data.donor;
  const fs = d.field_sources || {};
  const fm = d.field_source_meta || {};
  const vdSaid = (d.vin_decode || {}).fields || {};
  // WHAT THE DECODE SAID, kept beside a value a person changed — so the disagreement is on the
  // record rather than lost the moment somebody fixes it.
  const decodeNote = (key) => {
    const said = vdSaid[key];
    if (!said || String(said).trim().toLowerCase() === String(d[key] || "").trim().toLowerCase()) return null;
    return (
      <div data-testid={`decode-said-${key}`} className="text-[10px] text-amber-700 font-mono mt-0.5">
        decode said {said}
      </div>
    );
  };
  const vehicle = [d.year, titleCase(d.make), titleCase(d.model)].filter(Boolean).join(" ") || "Unspecified vehicle";
  // Carry "you came from this truck" onward, so opening a part can bring you back here.
  const originState = fromState(location, vehicle);

  return (
    <div data-testid="donor-detail-page" className="space-y-5 max-w-5xl">
      <BackLink to="/donors" label="Vehicles & Equipment" current={vehicle} testid="donor-detail-back" />

      {mergedFrom && (
        <div data-testid="donor-merged-redirect-note" className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <GitMerge className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">You scanned <span className="font-mono font-bold">{mergedFrom}</span>, which was merged into <span className="font-mono font-bold">{d.stock_number}</span> — showing the current record. That old tag can stay on the truck; it will always land here.</p>
        </div>
      )}

      {/* HIS ADDITION, 2026-10-09: *"if a truck's details are corrected after its tag was printed,
          the tag on the truck is now wrong. Flag it — 'tag out of date — reprint?'"* It appears
          only when the correction touched something the sticker actually SHOWS, and it clears
          itself when the browser confirms a new tag came out of the printer. */}
      {d.tag_stale_at && (
        <div data-testid="donor-tag-stale-banner" className="flex flex-wrap items-start gap-2 rounded-xl border border-amber-400 bg-amber-50 px-4 py-3">
          <Tag className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <b>Tag out of date — reprint?</b> The tag on this truck was printed before{" "}
            {(d.tag_stale_fields || []).map((f, i) => (
              <span key={f}>{i > 0 ? ", " : ""}the <b>{TAG_FIELD_LABELS[f] || f}</b> was corrected
                {d.tag_stale_was?.[f] ? <> (it said <span className="font-mono">{String(d.tag_stale_was[f])}</span>)</> : null}
              </span>
            ))}
            , so the sticker out there is wrong.
          </div>
          {canPrintTags && (
            <div className="ml-auto"><DonorTagButton donorId={id} stockNumber={d.stock_number} /></div>
          )}
        </div>
      )}

      {/* THE RECORD, then the ACTION: what happened to this truck is visible to anyone who can
          see it; splitting it back apart is Admin-only. */}
      <MergeHistory rows={data.merge_history} />

      {/* ONE PRESS BACK, on the surviving truck's own page — and the offer says WHY it is gone
          once money has touched one of the parts the merge moved. */}
      <MergeUndoLine donorId={id} onDone={load} />

      {/* HEADER */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="h-1 bg-[#B01E28]" />
        <div className="p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 font-mono text-xs font-bold text-slate-500">
                <Truck className="w-4 h-4 text-[#B01E28]" />{d.stock_number}
              </div>
              <h1 data-testid="donor-detail-title" className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900 mt-1">{vehicle}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {canManage ? (
                  <Select value={d.status} onValueChange={setStage} disabled={stageSaving}>
                    <SelectTrigger data-testid="donor-detail-stage-select" className="h-7 w-auto rounded-md bg-slate-800 text-white text-[10px] uppercase font-mono border-0 px-2.5 gap-1.5">
                      <SelectValue>{donorStatusLabel(d.status)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent className="rounded-lg">
                      {DONOR_STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`stage-opt-${s}`} className="text-xs font-mono uppercase">{donorStatusLabel(s)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge data-testid="donor-detail-status" className="rounded-md bg-slate-800 text-white text-[10px] uppercase font-mono">{donorStatusLabel(d.status)}</Badge>
                )}
                <TruckRoleTag role={d.role} size="md" testid="donor-detail-role" />
                {d.role === "builder" && d.po_number && (
                  <Badge data-testid="donor-detail-po" className="rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] uppercase font-mono">
                    PO {d.po_number}
                  </Badge>
                )}
                {data.can_view_cost && data.builder_cost && (data.builder_cost.parts_in_count > 0 || d.role === "builder") && (
                  <Badge data-testid="donor-detail-builder-cost-chip" className="rounded-md bg-slate-900 text-white text-[10px] uppercase font-mono">
                    <Wrench className="w-3 h-3 mr-1" />Cost-to-date {money(data.builder_cost.cost_to_date)}
                  </Badge>
                )}
                {canManage && (
                  <Button data-testid="donor-detail-role-switch" size="sm" variant="outline"
                    onClick={() => { const t = d.role === "builder" ? "donor" : "builder"; setPoInput(t === "builder" ? (d.po_number || "") : ""); setRoleConfirm(t); }}
                    className="rounded-md h-7 px-2.5 text-[11px] border-slate-300 hover:bg-slate-50">
                    <RefreshCw className="w-3 h-3 mr-1" />{d.role === "builder" ? "Switch to Parts" : "Switch to Builder"}
                  </Button>
                )}
                <span className="font-mono text-xs text-slate-500 flex items-center gap-1">VIN {d.vin || "—"}</span>
                {/* B1 — THE VIN CHECK DIGIT. FLAG, NEVER REJECT (his ruling): older, imported and
                    some trailer VINs are legitimately non-compliant, so this never blocks
                    anything. It is here, beside the VIN, because one misread character off a
                    dirty plate is what it catches. */}
                {d.vin_check && !d.vin_check.ok && (
                  <span data-testid="donor-vin-check-flag" title={d.vin_check.note}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#C76714]">
                    <AlertTriangle className="w-3.5 h-3.5" />{d.vin_check.note}
                  </span>
                )}
                {Object.values(fs).includes("decoded") && <SourceChip src="decoded" />}
              </div>
            </div>
            {(canManage || canRequest || canPrintTags || canCaptureParts || (canScrap && d.status !== "scrapped")) && (
              <div className="flex flex-wrap gap-2 items-center">
                {canManage && (
                  <Button data-testid="donor-detail-edit" onClick={() => navigate(`/donors/${id}/edit`, { state: originState })} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white h-9"><Pencil className="w-4 h-4 mr-1.5" />Edit vehicle</Button>
                )}
                {canCaptureParts && (
                  <Button data-testid="donor-detail-new-part" onClick={() => setNewPartOpen(true)} className="rounded-lg bg-[#B01E28] hover:bg-[#8B1820] text-white h-9"><PackagePlus className="w-4 h-4 mr-1.5" />New part</Button>
                )}
                {canManage && (
                  <PhotoUploader endpoint={`/donors/${id}/photos`} onUploaded={addDonorPhoto} onFailed={bumpRefused} testidPrefix="donor-detail-photos" size="md" />
                )}
                <MoreActions testid="donor-detail-more">
                  <SalesMenuButton kind="truck" id={id} label={d.stock_number || "this truck"} />
                  {canRequest && (
                    <Button data-testid="donor-detail-request" variant="outline" onClick={() => navigate(`/requests?new=verify&donor=${encodeURIComponent(d.stock_number || d.id)}`)} className="rounded-lg border-slate-300 h-9"><Inbox className="w-4 h-4 mr-1.5" />Request / Verify</Button>
                  )}
                  {canPrintTags && <DonorTagButton donorId={id} stockNumber={d.stock_number} />}
                  {canManage && <PhotoUploader variant="document" multiple={false} endpoint={`/donors/${id}/build-sheet`} buttonLabel="Build Sheet" onUploaded={(r) => { (r?.read_exactly === false ? toast.warning : toast.success)(r?.message || "Build sheet attached"); silentReload(); }} onFailed={bumpRefused} spreadsheetTo={(file) => setDealerSheet({ file, at: Date.now() })} testidPrefix="donor-detail-buildsheet" />}
                  {canManage && <YardSectorMove kind="donor" donorId={id} onDone={load} testid="donor-yard-move" />}
                  {/* "This is a duplicate of…" lives in the ⋯ menu, with void tag / un-scrap /
                      record source truck: "someone looking for 'fix a mistake on this record'
                      should find them all in the same place." It is the ONLY way a VIN-LESS
                      double entry is ever found — nothing can detect those. Admin-only, and
                      absent (not greyed) for everyone else. */}
                  <DuplicateOfAction donor={d} onDone={load} />
                  {canScrap && d.status !== "scrapped" && (
                    <ScrapTruckDialog donor={d} onDone={load} trigger={
                      <Button data-testid="donor-detail-scrap-truck" variant="outline" className="rounded-lg border-[#B01E28] text-[#B01E28] hover:bg-red-50 h-9"><AlertTriangle className="w-4 h-4 mr-1.5" />Scrap remaining truck</Button>} />
                  )}
                </MoreActions>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t border-slate-100">
            <Field label="Year" value={d.year} chip={<SourceChip src={fs.year} meta={fm.year} />} note={decodeNote("year")} />
            <Field label="Make" value={d.make ? titleCase(d.make) : ""} chip={<SourceChip src={fs.make} meta={fm.make} />} note={decodeNote("make")} />
            <Field label="Model" value={d.model ? titleCase(d.model) : ""} chip={<SourceChip src={fs.model} meta={fm.model} />} note={decodeNote("model")} />
            <Field label="Engine" value={d.engine_model} chip={<SourceChip src={fs.engine_model} meta={fm.engine_model} />} note={decodeNote("engine_model")} />
            <Field label="Mileage" value={d.mileage} mono />
            <Field label="Engine hours" value={d.hours} mono />
            {/* the PLACE, not its database id — this printed a raw ObjectId, so a truck that was
                correctly placed read as gibberish and "no location" couldn't be checked here */}
            <Field label="Site / location"
              value={fullLabel(d.location) || d.location_label || (d.site_location_id ? "placed — location record missing" : "not placed — nobody can find it")} />
            <Field label="Added" value={formatDate(d.created_at)} mono />
          </div>
        </div>
      </div>

      {/* BUILD SHEET */}
      <SectionCard testid="donor-detail-specs" title="Vehicle specs" icon={Wrench} collapsibleId="specs" defaultOpen>
        <TruckSpecsPanel donorId={id} canEdit={false} donor={d} onCategoryChange={load} />
      </SectionCard>

      {canCaptureParts && (
        <CreatePartFromTruck open={newPartOpen} onOpenChange={setNewPartOpen} donorId={id} donorStock={d.stock_number}
          onCreated={() => { setNewPartOpen(false); load(); }} />
      )}

      <SectionCard testid="donor-detail-buildsheet" title="Build sheet" icon={FileText} collapsibleId="buildsheet" defaultOpen={false}>
        {d.build_sheet ? (
          <BuildSheetViewer buildSheet={d.build_sheet} testid="donor-buildsheet-viewer" />
        ) : (
          <div data-testid="donor-buildsheet-empty" className="flex flex-col items-center gap-2 text-sm text-slate-400 py-6 text-center">
            <FileText className="w-5 h-5" />
            No build sheet attached
            {canManage && <span className="text-xs text-slate-400">Use the “Build Sheet” button above to attach an as-built PDF or image — or a Freightliner dealer sheet (Excel or .csv), which is read exactly first.</span>}
          </div>
        )}
        <div className="mt-3"><RefusedUploads params={{ donor_id: id, where: "truck_build_sheet" }} refreshKey={refusedTick} testid="donor-buildsheet-refused" /></div>
      </SectionCard>

      {/* ENGINE DATAPLATE — the maker's own printed record of the engine in this truck. Read off
          their PDF exactly; the plate stays tied to this truck even after the engine is pulled. */}
      <SectionCard testid="donor-detail-dataplate" title="Engine dataplate" icon={Cpu} collapsibleId="dataplate" defaultOpen={false}>
        <DataplatePanel kind="donor" id={id} canManage={canManage} canVerify={canCaptureParts || canManage} testid="donor-dataplate" />
      </SectionCard>

      {/* EXACT BUILD-SHEET READERS — the maker's own file, read character for character. Count
          first: the upload shows what WOULD happen and writes nothing until he presses. */}
      <SectionCard testid="donor-detail-sheet-readers" title="Dealer sheet readers (exact)" icon={FileSpreadsheet} collapsibleId="sheetreaders" defaultOpen={false} openSignal={dealerSheet?.at}>
        <SheetReaderPanel donorId={id} canManage={canManage} canSeeKeyCode={has("view_key_codes")} incoming={dealerSheet} onApplied={silentReload} testid="donor-sheet-reader" />
      </SectionCard>

      {/* BATCH 4 — what the AI's reads say this truck has to sell (kept, read only) and its keywords (tap to add) */}
      <SectionCard testid="donor-detail-ai-lists" title="Sellable parts (AI) · Keywords" icon={Sparkles} collapsibleId="ailists" defaultOpen>
        <div className="space-y-4">
          <TruckSellableList list={d.sellable_list} testid="donor-sellable" />
          <TruckKeywords key={d.id} donorId={d.id} keywords={d.keywords} suggestions={d.keyword_suggestions} canEdit={canManage} testid="donor-keywords" />
        </div>
      </SectionCard>

      {/* PHOTOS */}
      <SectionCard testid="donor-detail-photos" title="Photos" icon={Camera} collapsibleId="photos" defaultOpen>
        <div className="mb-2"><RefusedUploads params={{ donor_id: id, where: "truck_photo" }} refreshKey={refusedTick} testid="donor-photos-refused" /></div>
        <p className="text-[11px] text-slate-400 mb-2">Check-in / condition photos of the whole truck. These stay here and never enter the parts-matching pool. Hover a photo → <span className="text-[#C76714] font-semibold">add it to a specific part</span> if it clearly shows one.</p>
        {(() => {
          const active = (d.photos || []).filter((p) => !p.is_archived);
          const isDismantle = (p) => p.photo_kind === "pool" || !!p.session_id;
          const nCheck = active.filter((p) => !isDismantle(p)).length;
          const nDis = active.filter(isDismantle).length;
          const shown = photoFilter === "all" ? d.photos
            : (d.photos || []).filter((p) => (photoFilter === "dismantle") === isDismantle(p));
          return (
            <>
              {nCheck > 0 && nDis > 0 && (
                <div data-testid="donor-photo-filter" className="flex gap-1.5 mb-3">
                  {[["all", `All (${active.length})`], ["checkin", `Check-in (${nCheck})`], ["dismantle", `Dismantle (${nDis})`]].map(([k, lbl]) => (
                    <button key={k} data-testid={`donor-photo-filter-${k}`} onClick={() => setPhotoFilter(k)}
                      className={`rounded-full px-3 py-1 text-[11px] font-mono font-semibold border transition-colors ${photoFilter === k ? "border-[#B01E28] bg-[#FDF3F4] text-[#B01E28]" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
              )}
              <PhotoGallery photos={shown} testid="donor-photos" canManage={canArchivePhotos} showKind
                fixScope={canArchivePhotos ? "donor" : undefined} fixRecordId={id} onFixed={load}
                mainRecordId={canArchivePhotos ? id : undefined}
                mainPhotoFileId={d.main_photo_file_id} resolvedMainFileId={d.main_photo?.file_id}
                onMainChanged={load}
                onAttachToPart={canCaptureParts && data.parts.length ? setAttachFileId : undefined} />
              <div className="mt-3">
                <PhotoMoveTrail scope="donor" recordId={id} onDone={load} testid="donor-photo-moves" />
              </div>
            </>
          );
        })()}
      </SectionCard>

      {/* PROMOTE CHECK-IN PHOTO → PART picker */}
      <Dialog open={!!attachFileId} onOpenChange={(o) => !o && setAttachFileId(null)}>
        <DialogContent data-testid="attach-to-part-dialog" className="bg-white">
          <DialogHeader>
            <DialogTitle>Add this check-in photo to a part</DialogTitle>
            <DialogDescription>A copy is attached to the part you pick. The photo stays on this truck too — nothing is moved or removed.</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
            {data.parts.length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center font-mono">NO PARTS ON THIS TRUCK YET</div>
            ) : data.parts.map((p) => (
              <button key={p.id} data-testid={`attach-part-option-${p.short_code}`} disabled={attaching}
                onClick={() => attachToPart(p.id)}
                className="w-full flex items-center gap-2 py-3 px-2 text-left hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-50">
                <Package className="w-4 h-4 text-[#B01E28] shrink-0" />
                <span className="font-mono text-xs font-bold w-24 shrink-0">{p.tag_code || p.short_code}</span>
                <span className="flex-1 text-sm text-slate-800 truncate">{p.name || <span className="text-slate-300">Unidentified</span>}</span>
                <Badge className={`rounded-md text-white text-[9px] uppercase font-mono ${partStatusColor(p)}`}>{partStatusLabel(p)}</Badge>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachFileId(null)} disabled={attaching} data-testid="attach-cancel">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PARTS FROM THIS DONOR */}
      <SectionCard testid="donor-detail-parts" title={`Parts removed from this truck (${data.part_count}${data.active_part_count != null && data.active_part_count !== data.part_count ? ` · ${data.active_part_count} active` : ""})`} icon={Package} collapsibleId="parts-out" defaultOpen>
        {canCaptureParts && d.role !== "builder" && (
          <div data-testid="donor-capture-panel" className="border-2 border-[#E0781A]/30 bg-[#FFF9F3] rounded-xl p-4 mb-4">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#C76714] mb-2">
              <Package className="w-3.5 h-3.5" />Capture a part off this truck — scan a pre-printed FAS tag
            </div>
            <ScanField onScan={captureIntoDonor} busy={capBusy} testid="donor-capture-scan" continuous
              label={null} placeholder="Scan / type FAS tag…" submitLabel="Capture" />
            <p className="text-[11px] text-slate-500 mt-2">Tag-first: it lands staged under this truck. Identify, grade &amp; value it later in Identify &amp; Completion.</p>
          </div>
        )}
        {data.parts.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center font-mono">NO PARTS YET</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.parts.map((p) => {
              const consumed = isConsumedPart(p);
              return (
              <div key={p.id} data-testid={`donor-part-${p.short_code}`} data-back-row data-consumed={consumed ? "1" : "0"}
                className={`flex flex-col md:flex-row md:items-center gap-1.5 md:gap-3 py-3 hover:bg-slate-50/70 -mx-2 px-2 rounded-lg transition-colors ${consumed ? "opacity-55 grayscale-[35%]" : ""}`}>
                <span onClick={() => navigate(`/parts/${p.id}`, { state: originState })} className="font-mono text-xs font-bold flex items-center gap-1 md:w-28 cursor-pointer hover:text-[#B01E28]"><Package className="w-3.5 h-3.5 text-[#B01E28]" />{p.tag_code || p.short_code}</span>
                <span onClick={() => navigate(`/parts/${p.id}`, { state: originState })} className={`flex-1 text-sm font-medium cursor-pointer hover:underline ${consumed ? "text-slate-500 line-through decoration-slate-300" : "text-slate-800"}`}>{p.name} <span className="font-mono text-[11px] text-slate-400">{p.brand}</span></span>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={`rounded-md text-white text-[9px] uppercase font-mono ${partStatusColor(p)}`}>{partStatusLabel(p)}</Badge>
                  {p.scrap?.recycle_batch_number && <span className="font-mono text-[9px] text-slate-500" data-testid={`donor-part-batch-${p.short_code}`}>· {p.scrap.recycle_batch_number}</span>}
                  {p.reserved_cart && <Badge className="rounded-md bg-amber-100 text-amber-800 border border-amber-200 text-[9px] uppercase font-mono"><ShoppingCart className="w-3 h-3 mr-1" />In Cart{p.reserved_cart.po_number ? ` · PO ${p.reserved_cart.po_number}` : ""}</Badge>}
                  {p.grade && <span className="font-mono text-[10px] text-slate-500">Grade {p.grade}</span>}
                  {p.location && <WhereItIs location={p.location} size="sm" testid={`donor-part-where-${p.short_code}`} />}
                  {canScrap && !["scrap", "defective", "core", "recycled", "archived"].includes(p.status) && (
                    <ScrapDialog part={p} onDone={load} trigger={
                      <button data-testid={`donor-part-scrap-${p.short_code}`} className="text-[#B01E28] hover:bg-red-50 rounded p-1" title="Scrap this part"><Trash2 className="w-3.5 h-3.5" /></button>} />
                  )}
                </div>
              </div>
            );})}
          </div>
        )}
      </SectionCard>

      {/* PARTS PULLED INTO THIS BUILD (builder trucks) — list only, no cost total */}
      {(d.role === "builder" || data.parts_in_count > 0) && (
        <SectionCard testid="donor-detail-parts-in" title={`Parts pulled into this build (${data.parts_in_count})`} icon={Wrench} collapsibleId="parts-in" defaultOpen>
          {(data.parts_in || []).length === 0 ? (
            <div data-testid="parts-in-empty" className="text-sm text-slate-400 py-4 text-center font-mono">NO PARTS PULLED IN YET</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.parts_in.map((p) => (
                <div key={p.id} data-testid={`parts-in-${p.short_code}`} data-back-row
                  className="flex flex-col md:flex-row md:items-center gap-1.5 md:gap-3 py-3 hover:bg-slate-50/70 -mx-2 px-2 rounded-lg transition-colors cursor-pointer" onClick={() => navigate(`/parts/${p.id}`, { state: originState })}>
                  <span className="font-mono text-xs font-bold flex items-center gap-1 md:w-28 hover:text-[#B01E28]"><Package className="w-3.5 h-3.5 text-[#B01E28]" />{p.tag_code || p.short_code}</span>
                  <span className="flex-1 text-sm font-medium text-slate-800 hover:underline">{p.name} <span className="font-mono text-[11px] text-slate-400">{p.brand}</span></span>
                  <div className="flex flex-wrap items-center gap-2">
                    {p.po_reference && <span className="font-mono text-[10px] text-indigo-600">{p.po_reference}</span>}
                    <Badge className={`rounded-md text-white text-[9px] uppercase font-mono ${partStatusColor(p)}`}>{partStatusLabel(p)}</Badge>
                    {p.removed_at && <span className="font-mono text-[10px] text-slate-400">{(p.removed_at || "").slice(0, 10)}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p data-testid="parts-in-gap-note" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-3">
            Note: this list shows only parts pulled via a cart that <b>named this builder</b>. Parts added through the generic “Add to Cart” path without selecting this builder won’t carry the builder link and won’t appear here — so this list may be incomplete.
          </p>
        </SectionCard>
      )}

      {/* FINANCIALS — cost-walled */}
      {data.can_view_cost && (
        <SectionCard testid="donor-detail-financials" title="Financials — acquisition &amp; recovery" icon={DollarSign}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Field label="Purchase price" value={money(d.purchase_price)} mono />
            <Field label="Landed cost" value={money(d.landed_cost)} mono />
            <Field label="Starting cost (total)" value={money(d.starting_cost)} mono />
          </div>
          {data.recovery && (
            <div data-testid="donor-detail-recovery" className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Recovery to date</span>
                <Link to="/yard-production" className="text-[11px] text-[#E0781A] hover:underline">Yard Metrics →</Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <div className="text-[10px] uppercase text-slate-400">Avoided cost</div>
                  <div className="font-mono font-bold text-sm">{money(data.recovery.avoided_cost)}</div>
                  {data.recovery.internal_used > 0 && <div className={`text-[10px] ${data.recovery.coverage_pct < 100 ? "text-[#B01E28]" : "text-slate-400"}`}>{data.recovery.internal_with_ref}/{data.recovery.internal_used} w/ OEM ref ({data.recovery.coverage_pct}%)</div>}
                </div>
                <div><div className="text-[10px] uppercase text-slate-400">Cash</div><div className="font-mono font-bold text-sm">{money(data.recovery.cash)}</div></div>
                <div><div className="text-[10px] uppercase text-slate-400">Remaining</div><div className="font-mono font-bold text-sm">{money(data.recovery.remaining_to_recover)}</div></div>
                <div>
                  <div className="text-[10px] uppercase text-slate-400">Net position</div>
                  <div className={`font-mono font-bold text-sm ${data.recovery.net_position >= 0 ? "text-[#16A34A]" : "text-[#B01E28]"}`}>{money(data.recovery.net_position)}</div>
                  <Badge className={`mt-0.5 rounded text-[8px] font-mono uppercase ${data.recovery.in_black ? "bg-emerald-100 text-emerald-700 border border-emerald-200" : "bg-red-50 text-[#B01E28] border border-red-200"}`}>{data.recovery.in_black ? "In the black" : "In the red"}</Badge>
                </div>
              </div>
            </div>
          )}
          {/* THE NUMBER MUST NOT LOOK BROKEN. "If DNR-00108 now shows a part that sold for $900 but
              that $900 sits in the legacy bucket, the truck page has to say so — otherwise someone
              adds up the truck's parts and the number doesn't match its recovery, and it looks
              like a bug." (owner, 2026-09-28) */}
          {(data.recovery_booked_as_legacy || []).length > 0 && (
            <div data-testid="donor-recovery-legacy-note"
              className="mt-4 pt-4 border-t border-slate-100 rounded-lg bg-amber-50 border border-amber-200 p-3">
              <p className="text-xs font-bold text-amber-900">
                {data.recovery_booked_as_legacy.length} part{data.recovery_booked_as_legacy.length === 1 ? "" : "s"} on
                this truck sold or was used BEFORE anybody traced it here
              </p>
              <p className="text-[11px] text-amber-900/80 mt-1">
                Their recovery stays booked as LEGACY — the figures above deliberately exclude them, so
                nothing was back-dated into a period you have already read. The record still says the
                part came off this truck, which is the point.
              </p>
              <p className="text-[11px] text-amber-900/80 mt-1 font-mono">
                {data.recovery_booked_as_legacy.map((x) => x.tag_code || x.short_code).filter(Boolean).join(" · ")}
              </p>
            </div>
          )}
          {data.builder_cost && (data.builder_cost.parts_in_count > 0 || d.role === "builder") && (
            <div data-testid="donor-detail-builder-cost" className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1"><Wrench className="w-3 h-3" />Builder cost-to-date (parts in)</span>
                <Link to="/yard-production" className="text-[11px] text-[#E0781A] hover:underline">Yard Metrics →</Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div><div className="text-[10px] uppercase text-slate-400">Truck cost</div><div className="font-mono font-bold text-sm">{money(data.builder_cost.truck_cost)}</div></div>
                <div>
                  <div className="text-[10px] uppercase text-slate-400">Parts in ({data.builder_cost.parts_in_count})</div>
                  <div className="font-mono font-bold text-sm">{money(data.builder_cost.parts_value)}</div>
                  {data.builder_cost.parts_in_count > 0 && <div className={`text-[10px] ${data.builder_cost.valued_coverage_pct < 100 ? "text-[#B01E28]" : "text-slate-400"}`}>{data.builder_cost.parts_valued}/{data.builder_cost.parts_in_count} valued ({data.builder_cost.valued_coverage_pct}%)</div>}
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-400">Cost-to-date</div>
                  <div className="font-mono font-bold text-sm text-slate-900">{money(data.builder_cost.cost_to_date)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-400">Basis mix</div>
                  <div className="font-mono text-[11px] text-slate-600">
                    {Object.entries(data.builder_cost.basis_composition).filter(([, n]) => n > 0).map(([b, n]) => `${n} ${b}`).join(" · ") || "—"}
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Derived read of parts assigned INTO this truck — nothing is stored on the builder; kept separate from donor recovery.</p>
            </div>
          )}
        </SectionCard>
      )}

      {/* NOTES — unified append-only log */}
      <SectionCard testid="donor-detail-notes" title="Notes" icon={FileText}>
        <NotesLog recordType="donor" recordId={id} canAdd={has("manage_donors")} />
      </SectionCard>

      {/* Role-switch confirmation */}
      <Dialog open={!!roleConfirm} onOpenChange={(v) => !v && setRoleConfirm(null)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="role-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><RefreshCw className="w-4 h-4 text-[#B01E28]" />Switch role to {roleConfirm === "builder" ? "Builder" : "Parts"}?</DialogTitle>
            <DialogDescription>
              {roleConfirm === "builder"
                ? `${d.stock_number} will become a BUILDER. Its costs/recovery will be tracked as cost accumulating INTO it (parts assigned in + its own truck cost), instead of cost recovered as parts leave. History is preserved.`
                : `${d.stock_number} will become PARTS. Its costs/recovery will be tracked as cost recovered as parts leave it, instead of cost accumulating into it. Its PO stays on record. History is preserved.`}
            </DialogDescription>
          </DialogHeader>
          {roleConfirm === "builder" && (
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Purchase Order (PO) — required</label>
              <Input data-testid="role-po-input" value={poInput} onChange={(e) => setPoInput(e.target.value)}
                placeholder="PO from the invoicing system" className="rounded-lg h-9 text-sm font-mono" />
              <p className="text-[11px] text-slate-400">A builder owns one permanent PO (entered once here). It auto-fills wherever this builder is selected, so it can never mismatch. Kept as history even if the role changes later.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setRoleConfirm(null)}>Cancel</Button>
            <Button data-testid="role-confirm-yes" disabled={switching} onClick={() => switchRole(roleConfirm)} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
              {switching ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Yes, switch to {roleConfirm === "builder" ? "Builder" : "Parts"}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
